import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, X, Clock, Flame } from 'lucide-react';
import useRitualEngineStore from '../../stores/useRitualEngineStore';
import './RitualTriggerOverlay.css';

/**
 * RitualTriggerOverlay — Modal that appears when an event-triggered ritual fires.
 *
 * Listens for a global `ritual-trigger` custom event dispatched by TimerEngine.
 * Shows the ritual with Start / Skip / Snooze options.
 */
export default function RitualTriggerOverlay() {
  const navigate = useNavigate();
  const definitions = useRitualEngineStore(s => s.definitions);
  const isCompletedToday = useRitualEngineStore(s => s.isCompletedToday);
  const getRitualStreak = useRitualEngineStore(s => s.getRitualStreak);

  const [triggered, setTriggered] = useState(null); // { ritualId, eventType }
  const [snoozed, setSnoozed] = useState(false);
  const snoozeTimerRef = useRef(null);

  // Listen for trigger events from TimerEngine
  useEffect(() => {
    const handler = (e) => {
      const { eventType } = e.detail;
      // Find matching ritual definitions with event triggers
      const matching = definitions.find(d =>
        !d.archived &&
        d.trigger === 'event' &&
        d.eventTrigger === eventType &&
        !isCompletedToday(d.id)
      );

      if (matching) {
        setTriggered({ ritualId: matching.id, eventType });
        setSnoozed(false);
      }
    };

    window.addEventListener('ritual-trigger', handler);
    return () => window.removeEventListener('ritual-trigger', handler);
  }, [definitions, isCompletedToday]);

  const ritual = triggered
    ? definitions.find(d => d.id === triggered.ritualId)
    : null;

  const handleStart = useCallback(() => {
    if (!ritual) return;
    const path = ritual.type === 'timed'
      ? `/rituals/${ritual.id}/timed`
      : `/rituals/${ritual.id}/run`;
    setTriggered(null);
    navigate(path);
  }, [ritual, navigate]);

  const handleSkip = useCallback(() => {
    setTriggered(null);
  }, []);

  const handleSnooze = useCallback(() => {
    setSnoozed(true);
    const currentTrigger = triggered;
    setTriggered(null);

    // Re-trigger after 5 minutes
    snoozeTimerRef.current = setTimeout(() => {
      setTriggered(currentTrigger);
      setSnoozed(false);
    }, 5 * 60 * 1000);
  }, [triggered]);

  // Cleanup snooze timer on unmount
  useEffect(() => {
    return () => clearTimeout(snoozeTimerRef.current);
  }, []);

  if (!ritual || !triggered) return null;

  const totalDuration = ritual.steps?.reduce((s, st) => s + (st.duration || 0), 0) || 0;
  const streak = getRitualStreak(ritual.id);

  return (
    <div className="ritual-trigger-overlay" onClick={handleSkip}>
      <div className="ritual-trigger-card animate-fade-in-scale" onClick={e => e.stopPropagation()}>
        <div className="ritual-trigger-card__glow" />

        <span className="ritual-trigger-card__emoji">{ritual.emoji}</span>
        <h3 className="ritual-trigger-card__name">{ritual.name}</h3>
        <p className="ritual-trigger-card__desc">{ritual.description}</p>

        <div className="ritual-trigger-card__meta">
          {totalDuration > 0 && (
            <span className="ritual-trigger-card__meta-item">
              <Clock size={14} /> {Math.ceil(totalDuration / 60)} min
            </span>
          )}
          {streak > 0 && (
            <span className="ritual-trigger-card__meta-item">
              <Flame size={14} /> {streak}d streak
            </span>
          )}
        </div>

        <div className="ritual-trigger-card__actions">
          <button className="btn btn-primary btn-lg ritual-trigger-card__start" onClick={handleStart}>
            <Play size={16} /> Start Ritual
          </button>
          <div className="ritual-trigger-card__secondary">
            <button className="btn btn-ghost btn-sm" onClick={handleSnooze}>
              Snooze 5min
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleSkip}>
              <X size={14} /> Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
